import React, { useEffect } from 'react';

const FactoryMethodPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Factory Method Pattern - Creational Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Factory Method Pattern - create objects without specifying their exact classes. Learn with real-world examples and implementation approaches.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Factory Method Pattern - create objects without specifying their exact classes. Learn with real-world examples and implementation approaches.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'factory method pattern, design patterns, creational patterns, object creation, polymorphism, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'factory method pattern, design patterns, creational patterns, object creation, polymorphism, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Factory Method Pattern</h1>
        <p style={styles.subtitle}>
          Creational Design Pattern - Create objects without specifying exact classes
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Factory Method pattern provides an interface for creating objects in a superclass, 
            but allows subclasses to alter the type of objects that will be created. It defines 
            an interface for creating an object, but lets subclasses decide which class to instantiate.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to create objects, but you don't know exactly which class you need to instantiate 
            until runtime. You want to provide a way to create objects without specifying their exact 
            classes, allowing for flexibility and extensibility.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Document Creation</h2>
          <div style={styles.codeBlock}>
{`// Product interface
interface Document {
    void open();
    void save();
    void close();
}

// Concrete products
class WordDocument implements Document {
    @Override
    public void open() {
        System.out.println("Opening Word document");
    }
    
    @Override
    public void save() {
        System.out.println("Saving Word document");
    }
    
    @Override
    public void close() {
        System.out.println("Closing Word document");
    }
}

class PDFDocument implements Document {
    @Override
    public void open() {
        System.out.println("Opening PDF document");
    }
    
    @Override
    public void save() {
        System.out.println("Saving PDF document");
    }
    
    @Override
    public void close() {
        System.out.println("Closing PDF document");
    }
}

class ExcelDocument implements Document {
    @Override
    public void open() {
        System.out.println("Opening Excel document");
    }
    
    @Override
    public void save() {
        System.out.println("Saving Excel document");
    }
    
    @Override
    public void close() {
        System.out.println("Closing Excel document");
    }
}

// Creator abstract class
abstract class DocumentCreator {
    // Factory method - to be implemented by subclasses
    public abstract Document createDocument();
    
    // Template method using the factory method
    public void processDocument() {
        Document doc = createDocument();
        doc.open();
        doc.save();
        doc.close();
    }
}

// Concrete creators
class WordDocumentCreator extends DocumentCreator {
    @Override
    public Document createDocument() {
        return new WordDocument();
    }
}

class PDFDocumentCreator extends DocumentCreator {
    @Override
    public Document createDocument() {
        return new PDFDocument();
    }
}

class ExcelDocumentCreator extends DocumentCreator {
    @Override
    public Document createDocument() {
        return new ExcelDocument();
    }
}

// Usage
public class FactoryMethodDemo {
    public static void main(String[] args) {
        DocumentCreator wordCreator = new WordDocumentCreator();
        wordCreator.processDocument();
        
        DocumentCreator pdfCreator = new PDFDocumentCreator();
        pdfCreator.processDocument();
        
        DocumentCreator excelCreator = new ExcelDocumentCreator();
        excelCreator.processDocument();
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Flexibility:</strong> Easy to add new product types</li>
            <li style={styles.listItem}><strong>Loose Coupling:</strong> Client code doesn't depend on concrete classes</li>
            <li style={styles.listItem}><strong>Single Responsibility:</strong> Object creation logic is separated</li>
            <li style={styles.listItem}><strong>Open/Closed Principle:</strong> Open for extension, closed for modification</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to delegate object creation to subclasses</li>
            <li style={styles.listItem}>When you don't know which exact class you need to instantiate</li>
            <li style={styles.listItem}>When you want to provide flexibility for future extensions</li>
            <li style={styles.listItem}>When you need to separate object creation from object usage</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Factory Method pattern is a fundamental creational pattern that provides a clean way 
            to create objects without specifying their exact classes. It promotes loose coupling and 
            makes the code more flexible and maintainable by allowing easy addition of new product types.
          </p>
          <p style={styles.paragraph}>
            Use this pattern when you need to delegate object creation to subclasses or when you want 
            to make your code more flexible for future extensions. However, be mindful of the added 
            complexity and only use it when the benefits outweigh the costs.
          </p>
        </section>
      </main>
    </article>
  );
};

export default FactoryMethodPattern;